import React, { Component } from "react";
import * as d3 from "d3";

const cardBase = {
  border: "1px solid #333",
  borderRadius: 10,
  padding: 10,
  boxSizing: "border-box",
  minWidth: 0,
};

const titleTyp = {
  fontSize: 14,
  fontWeight: 700,
  color: "#222",
};

const titleRowTyp = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: 6,
};

const margin = { top: 10, right: 10, bottom: 24, left: 40 };

const MIN_ZOOM_SEC = 3600;
const INITIAL_VIEW_SEC = 86400;
const TARGET_TICKS = 4;

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** tzo is minutes, local time minus UTC (the opposite of getTimezoneOffset). */
function utcSecToLocalDate(utcSec, tzo) {
  return new Date((Number(utcSec) + (Number(tzo) || 0) * 60) * 1000);
}

function localDateToUtcSec(date, tzo) {
  return date.getTime() / 1000 - (Number(tzo) || 0) * 60;
}

function formatMinuteTick(ts, tzo) {
  const d = utcSecToLocalDate(ts, tzo);
  const h = d.getUTCHours();
  const m = d.getUTCMinutes();
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 || 12;
  return `${h12}:${String(m).padStart(2, "0")}${ampm}`;
}

function formatDayTick(ts, tzo) {
  const local = Math.floor(Number(ts) + ((Number(tzo) || 0) * 60));
  if (!Number.isFinite(local)) return "";
  const day = ((local % 86400) + 86400) % 86400;
  const h24 = Math.floor(day / 3600);
  const ampm = h24 >= 12 ? "PM" : "AM";
  const h12 = h24 % 12 || 12;
  return `${h12}${ampm}`;
}

function formatWeekTick(ts, tzo) {
  const d = utcSecToLocalDate(ts, tzo);
  return `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}`;
}

function pickIntervalForTarget(range, step, target) {
  const steps = [];
  let n = step;
  while (n <= range) {
    steps.push(n);
    n += step;
  }
  if (steps.length === 0) return step;
  let best = steps[0];
  let bestDiff = Math.abs(range / best - target);
  for (const s of steps) {
    const diff = Math.abs(range / s - target);
    if (diff < bestDiff) {
      bestDiff = diff;
      best = s;
    }
  }
  return best;
}

function buildSubDayTickValues(x0, x1, tzo) {
  const range = x1 - x0;
  const step = pickIntervalForTarget(range, 60, TARGET_TICKS);
  const start = Math.ceil(x0 / step) * step;
  const ticks = [];
  for (let t = start; t <= x1; t += step) {
    if (t >= x0 && t <= x1) ticks.push(t);
  }
  const format =
    range <= MIN_ZOOM_SEC ? formatMinuteTick : formatDayTick;
  return { ticks, format: (v) => format(v, tzo) };
}

function buildXTickValues(xDomain, tzo) {
  const [x0, x1] = xDomain;
  if (!Number.isFinite(x0) || !Number.isFinite(x1) || x1 <= x0) {
    return { ticks: [], format: () => "" };
  }

  const range = x1 - x0;

  if (range <= 86400) {
    return buildSubDayTickValues(x0, x1, tzo);
  }

  const start = utcSecToLocalDate(x0, tzo);
  const end = utcSecToLocalDate(x1, tzo);
  const days = pickIntervalForTarget(range / 86400, 1, TARGET_TICKS);
  const generator = d3.timeDay.every(Math.max(1, days));

  if (!generator) {
    return { ticks: [], format: () => "" };
  }

  const ticks = generator
    .range(start, end)
    .map((d) => localDateToUtcSec(d, tzo))
    .filter((t) => t >= x0 && t <= x1);

  return { ticks, format: (v) => formatWeekTick(v, tzo) };
}

function padYDomain(domain) {
  const [lo, hi] = domain;
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) return [0, 1];
  if (lo === hi) {
    const pad = lo === 0 ? 1 : Math.abs(lo) * 0.1;
    return [lo - pad, hi + pad];
  }
  const pad = (hi - lo) * 0.1;
  return [lo - pad, hi + pad];
}

function lightBgFromColor(color) {
  return d3.interpolateRgb(color || "steelblue", "#ffffff")(0.9);
}

function formatHoverDate(ts, tzo) {
  const d = utcSecToLocalDate(ts, tzo);
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function formatHoverTime(ts, tzo) {
  const d = utcSecToLocalDate(ts, tzo);
  const h = d.getUTCHours();
  const min = String(d.getUTCMinutes()).padStart(2, "0");
  const sec = String(d.getUTCSeconds()).padStart(2, "0");
  return `${h}:${min}:${sec}`;
}

function pointInYRange(v, yDom) {
  if (!Array.isArray(yDom) || yDom.length < 2) return true;
  const lo = Number(yDom[0]);
  const hi = Number(yDom[1]);
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) return true;
  return v >= lo && v <= hi;
}

function isErrorValue(v) {
  return [1e4, 1e6].includes(+v);
}

function getFinitePoints(data) {
  return (data || []).filter(
    (p) =>
      p &&
      typeof p.t === "number" &&
      typeof p.v === "number" &&
      Number.isFinite(p.t) &&
      Number.isFinite(p.v)
  );
}

function dropErrorValues(points) {
  return points.filter((p) => !isErrorValue(p.v));
}

/** Smallest 1-2-5 step at or above the data. Sequence starts at 1. */
function nice125(maxVal) {
  if (!Number.isFinite(maxVal) || maxVal <= 1) return 1;
  const exp = Math.floor(Math.log10(maxVal));
  const base = 10 ** exp;
  const mant = maxVal / base;
  const step = mant <= 1 ? 1 : mant <= 2 ? 2 : mant <= 5 ? 5 : 10;
  return step * base;
}

function flowYDomain(points) {
  const cleaned = dropErrorValues(points);
  let maxV = 0;
  for (let i = 0; i < cleaned.length; i++) {
    if (cleaned[i].v > maxV) maxV = cleaned[i].v;
  }
  return [0, nice125(maxV)];
}

function pointsForGraph(data, yDom, autoRange) {
  const finite = getFinitePoints(data);
  if (autoRange) return dropErrorValues(finite);
  return finite.filter((p) => pointInYRange(p.v, yDom));
}

function getDataExtent(data, yDom, autoRange) {
  const valid = pointsForGraph(data, yDom, autoRange);
  if (valid.length === 0) return null;
  const ext = d3.extent(valid, (p) => p.t);
  if (!Number.isFinite(ext[0]) || !Number.isFinite(ext[1])) return null;
  if (ext[0] === ext[1]) {
    return [ext[0] - 1, ext[1] + 1];
  }
  return ext;
}

function buildGraphSeries(allPoints, x0, x1) {
  const sorted = [...allPoints].sort((a, b) => a.t - b.t);
  if (sorted.length === 0) return [];

  const bisect = d3.bisector((p) => p.t).left;
  const leftIdx = bisect(sorted, x0) - 1;
  const rightIdx = bisect(sorted, x1);
  const inRange = sorted.filter((p) => p.t >= x0 && p.t <= x1);
  const series = [];

  if (leftIdx >= 0) {
    series.push({ t: x0, v: sorted[leftIdx].v });
  }
  series.push(...inRange);
  if (rightIdx < sorted.length && sorted[rightIdx].t > x1) {
    series.push({ t: x1, v: sorted[rightIdx].v });
  }

  return series.sort((a, b) => a.t - b.t);
}

function clampViewDomain(domain, dataExtent) {
  if (!dataExtent || !domain) return domain;
  const [dataMin, dataMax] = dataExtent;
  const maxSpan = dataMax - dataMin;
  let [x0, x1] = domain;
  let span = x1 - x0;
  span = Math.max(MIN_ZOOM_SEC, Math.min(maxSpan, span));
  if (x1 - x0 !== span) {
    const mid = (x0 + x1) / 2;
    x0 = mid - span / 2;
    x1 = mid + span / 2;
  }
  if (x0 < dataMin) {
    x0 = dataMin;
    x1 = dataMin + span;
  }
  if (x1 > dataMax) {
    x1 = dataMax;
    x0 = dataMax - span;
  }
  return [x0, x1];
}

function initialViewDomain(dataExtent) {
  if (!dataExtent) return null;
  const [dataMin, dataMax] = dataExtent;
  const span = Math.min(INITIAL_VIEW_SEC, dataMax - dataMin);
  return [dataMax - span, dataMax];
}

function zoomViewDomain(domain, factor, anchorT, dataExtent) {
  if (!domain || !dataExtent) return domain;
  const [dataMin, dataMax] = dataExtent;
  const maxSpan = dataMax - dataMin;
  let [x0, x1] = domain;
  const span = x1 - x0;
  let newSpan = span * factor;
  newSpan = Math.max(MIN_ZOOM_SEC, Math.min(maxSpan, newSpan));
  const ratio = span > 0 ? (anchorT - x0) / span : 0.5;
  let newX0 = anchorT - ratio * newSpan;
  let newX1 = newX0 + newSpan;
  return clampViewDomain([newX0, newX1], dataExtent);
}

function panViewDomain(domain, deltaT, dataExtent) {
  if (!domain || !dataExtent) return domain;
  const [x0, x1] = domain;
  return clampViewDomain([x0 + deltaT, x1 + deltaT], dataExtent);
}

const crosshairTyp = {
  position: "absolute",
  width: 1,
  backgroundColor: "#666",
  pointerEvents: "none",
};

const hoverValueTyp = {
  position: "absolute",
  top: 0,
  fontSize: 11,
  color: "#222",
  pointerEvents: "none",
  lineHeight: 1.3,
};

export default class DoserGraph extends Component {
  static _clipSeq = 0;

  constructor(props) {
    super(props);
    this.svgRef = React.createRef();
    this.plotRef = React.createRef();
    this._clipId = `doser-graph-clip-${DoserGraph._clipSeq++}`;
    this.state = {
      hover: null,
      viewDomain: null,
      isPanning: false,
    };
    this._panStart = null;
    this._onDocumentMouseMove = this._onDocumentMouseMove.bind(this);
    this._onDocumentMouseUp = this._onDocumentMouseUp.bind(this);
  }

  componentDidMount() {
    this._syncViewDomainFromData();
    this._draw();
    this._attachWheel();
  }

  componentDidUpdate(prevProps) {
    const prevY = prevProps.yDom;
    const nextY = this.props.yDom;
    const yChanged =
      prevY !== nextY &&
      (!prevY ||
        !nextY ||
        prevY[0] !== nextY[0] ||
        prevY[1] !== nextY[1]);
    if (
      prevProps.data !== this.props.data ||
      yChanged ||
      prevProps.autoRange !== this.props.autoRange
    ) {
      this._syncViewDomainFromData();
    }
    this._draw();
    this._refreshHoverValue();
    this._attachWheel();
  }

  componentWillUnmount() {
    this._detachWheel();
    this._endPan();
  }

  _attachWheel() {
    const el = this.plotRef.current;
    if (!el || el === this._wheelEl) return;
    this._detachWheel();
    this._wheelEl = el;
    el.addEventListener("wheel", this._handleWheel, { passive: false });
  }

  _detachWheel() {
    if (this._wheelEl) {
      this._wheelEl.removeEventListener("wheel", this._handleWheel);
      this._wheelEl = null;
    }
  }

  _handleWheel = (e) => {
    e.preventDefault();
    const dataExtent = getDataExtent(this.props.data, this.props.yDom, this.props.autoRange);
    const { viewDomain } = this.state;
    if (!dataExtent || !viewDomain) return;

    const ctx = this._buildPlotContext();
    if (!ctx) return;

    const bounds = this.plotRef.current.getBoundingClientRect();
    const mouseX = e.clientX - bounds.left;
    const anchorT = ctx.x.invert(mouseX);
    const factor = e.deltaY > 0 ? 1.12 : 1 / 1.12;
    const next = zoomViewDomain(viewDomain, factor, anchorT, dataExtent);
    if (next) {
      this.setState({ viewDomain: next });
    }
  };

  _syncViewDomainFromData() {
    const dataExtent = getDataExtent(this.props.data, this.props.yDom, this.props.autoRange);
    if (!dataExtent) {
      if (this.state.viewDomain) {
        this.setState({ viewDomain: null });
      }
      return;
    }

    this.setState((prev) => {
      if (!prev.viewDomain) {
        return { viewDomain: initialViewDomain(dataExtent) };
      }
      return { viewDomain: clampViewDomain(prev.viewDomain, dataExtent) };
    });
  }

  _getViewDomain() {
    const dataExtent = getDataExtent(this.props.data, this.props.yDom, this.props.autoRange);
    if (!dataExtent) return null;
    return this.state.viewDomain || initialViewDomain(dataExtent);
  }

  _buildPlotContext() {
    const { yDom, width, height, data, autoRange } = this.props;
    const viewDomain = this._getViewDomain();
    if (!viewDomain) return null;

    const w = Math.max(0, Number(width) || 0);
    const h = Math.max(0, Number(height) || 0);
    const innerW = Math.max(0, w - margin.left - margin.right);
    const innerH = Math.max(0, h - margin.top - margin.bottom);

    const validPoints = pointsForGraph(data, yDom, autoRange);
    const [x0, x1] = viewDomain;
    const allSorted = [...validPoints].sort((a, b) => a.t - b.t);
    const sorted = buildGraphSeries(validPoints, x0, x1);
    const yDomain = autoRange
      ? flowYDomain(validPoints)
      : padYDomain(Array.isArray(yDom) ? yDom : [0, 1]);

    const x = d3
      .scaleLinear()
      .domain(viewDomain)
      .range([margin.left, w - margin.right]);

    const y = d3
      .scaleLinear()
      .domain(yDomain)
      .range([h - margin.bottom, margin.top]);

    return {
      w,
      h,
      innerW,
      innerH,
      xDomain: viewDomain,
      yDomain,
      x,
      y,
      sorted,
      hoverPoints: allSorted,
      hasData: sorted.length > 0,
    };
  }

  _valueAtPixel(mouseX, sorted, xScale) {
    if (!sorted.length) return null;
    if (sorted.length === 1) return sorted[0].v;

    const t = xScale.invert(mouseX);
    const bisect = d3.bisector((p) => p.t).left;
    const idx = bisect(sorted, t);

    if (idx <= 0) return sorted[0].v;
    if (idx >= sorted.length) return sorted[sorted.length - 1].v;

    const p0 = sorted[idx - 1];
    const p1 = sorted[idx];
    if (p1.t === p0.t) return p0.v;
    const frac = (t - p0.t) / (p1.t - p0.t);
    return p0.v + frac * (p1.v - p0.v);
  }

  _formatHoverValue(value) {
    if (value == null || !Number.isFinite(value)) return "";
    return Number(value).toFixed(2);
  }

  _buildHoverState(mouseX, ctx, tzo) {
    const value = this._valueAtPixel(mouseX, ctx.hoverPoints, ctx.x);
    const t = ctx.x.invert(mouseX);
    return {
      x: mouseX,
      label: this._formatHoverValue(value),
      dateStr: formatHoverDate(t, tzo),
      timeStr: formatHoverTime(t, tzo),
    };
  }

  _hoverStateChanged(prev, next) {
    return (
      !prev ||
      prev.x !== next.x ||
      prev.label !== next.label ||
      prev.dateStr !== next.dateStr ||
      prev.timeStr !== next.timeStr
    );
  }

  _refreshHoverValue() {
    const { hover } = this.state;
    if (!hover) return;

    const ctx = this._buildPlotContext();
    if (!ctx || !ctx.hasData) {
      this.setState({ hover: null });
      return;
    }

    const next = this._buildHoverState(hover.x, ctx, this.props.tzo);
    if (this._hoverStateChanged(hover, next)) {
      this.setState({ hover: next });
    }
  }

  _plotCoords(e) {
    const el = this.plotRef.current;
    if (!el) return null;
    const bounds = el.getBoundingClientRect();
    return {
      x: e.clientX - bounds.left,
      y: e.clientY - bounds.top,
    };
  }

  _isInPlotArea(coords, ctx) {
    if (!coords || !ctx) return false;
    return (
      coords.x >= margin.left &&
      coords.x <= ctx.w - margin.right &&
      coords.y >= margin.top &&
      coords.y <= ctx.h - margin.bottom
    );
  }

  _handleMouseMove(e) {
    if (this.state.isPanning) return;

    const ctx = this._buildPlotContext();
    const coords = this._plotCoords(e);
    if (!ctx || !coords || !ctx.hasData || !this._isInPlotArea(coords, ctx)) {
      if (this.state.hover) this.setState({ hover: null });
      return;
    }

    const next = this._buildHoverState(coords.x, ctx, this.props.tzo);
    if (this._hoverStateChanged(this.state.hover, next)) {
      this.setState({ hover: next });
    }
  }

  _handleMouseLeave() {
    if (!this.state.isPanning && this.state.hover) {
      this.setState({ hover: null });
    }
  }

  _handleMouseDown(e) {
    if (e.button !== 0) return;
    const ctx = this._buildPlotContext();
    const coords = this._plotCoords(e);
    if (!ctx || !coords || !this._isInPlotArea(coords, ctx)) return;

    e.preventDefault();
    this._panStart = {
      clientX: e.clientX,
      domain: [...this.state.viewDomain],
      pixelsPerSec: ctx.innerW / (ctx.xDomain[1] - ctx.xDomain[0]),
    };
    this.setState({ isPanning: true, hover: null });
    document.addEventListener("mousemove", this._onDocumentMouseMove);
    document.addEventListener("mouseup", this._onDocumentMouseUp);
  }

  _onDocumentMouseMove(e) {
    if (!this._panStart) return;
    const dataExtent = getDataExtent(this.props.data, this.props.yDom, this.props.autoRange);
    if (!dataExtent) return;

    const dx = e.clientX - this._panStart.clientX;
    const deltaT = -dx / this._panStart.pixelsPerSec;
    const next = panViewDomain(this._panStart.domain, deltaT, dataExtent);
    if (next) {
      this.setState({ viewDomain: next });
    }
  }

  _onDocumentMouseUp() {
    this._endPan();
  }

  _endPan() {
    if (this._panStart) {
      this._panStart = null;
      document.removeEventListener("mousemove", this._onDocumentMouseMove);
      document.removeEventListener("mouseup", this._onDocumentMouseUp);
    }
    if (this.state.isPanning) {
      this.setState({ isPanning: false });
    }
  }

  _draw() {
    const svgEl = this.svgRef.current;
    if (!svgEl) return;

    const { color, tzo } = this.props;
    const ctx = this._buildPlotContext();
    if (!ctx) {
      d3.select(svgEl).selectAll("*").remove();
      return;
    }

    const { w, h, innerW, innerH, x, y, sorted, hasData } = ctx;

    d3.select(svgEl).selectAll("*").remove();

    const svg = d3.select(svgEl);
    svg
      .attr("width", w)
      .attr("height", h)
      .style("display", "block")
      .style("overflow", "visible");

    const xTickInfo = buildXTickValues(ctx.xDomain, tzo);
    const xAxis = d3
      .axisBottom(x)
      .tickValues(xTickInfo.ticks)
      .tickFormat(xTickInfo.format);
    const yAxis = d3.axisLeft(y).ticks(5);

    if (!hasData) {
      xAxis.tickValues([]).ticks(0);
      yAxis.ticks(0);
    }

    svg
      .append("g")
      .attr("transform", `translate(0, ${h - margin.bottom})`)
      .call(xAxis)
      .selectAll("text")
      .style("font-size", "10px");

    svg
      .append("g")
      .attr("transform", `translate(${margin.left}, 0)`)
      .call(yAxis)
      .selectAll("text")
      .style("font-size", "10px");

    if (!hasData) {
      svg
        .append("text")
        .attr("x", margin.left + innerW / 2)
        .attr("y", margin.top + innerH / 2)
        .attr("text-anchor", "middle")
        .attr("dominant-baseline", "middle")
        .attr("fill", "#888")
        .style("font-size", "12px")
        .text("No Data");
      return;
    }

    const stroke = color || "steelblue";

    svg
      .append("defs")
      .append("clipPath")
      .attr("id", this._clipId)
      .append("rect")
      .attr("x", margin.left)
      .attr("y", margin.top)
      .attr("width", innerW)
      .attr("height", innerH);

    const series = svg.append("g").attr("clip-path", `url(#${this._clipId})`);

    if (sorted.length >= 2) {
      const line = d3
        .line()
        .curve(d3.curveMonotoneX)
        .x((p) => x(p.t))
        .y((p) => y(p.v));

      series
        .append("path")
        .datum(sorted)
        .attr("fill", "none")
        .attr("stroke", stroke)
        .attr("stroke-width", 2)
        .attr("d", line);
    } else {
      const p0 = sorted[0];
      series
        .append("circle")
        .attr("cx", x(p0.t))
        .attr("cy", y(p0.v))
        .attr("r", 3)
        .attr("fill", stroke);
    }
  }

  render() {
    const { title, width, height, color, unit } = this.props;
    const { hover, isPanning } = this.state;
    const graphH = Math.max(0, Number(height) || 0);
    const innerH = Math.max(0, graphH - margin.top - margin.bottom);
    const graphColor = color || "steelblue";
    const hoverBg = lightBgFromColor(graphColor);
//     <div style={titleTyp}>{title != null ? `${title} (${unit})` : ""}</div>
    return (
      <div style={cardBase}>
        <div style={titleRowTyp}>
          <div style={titleTyp}>{title != null ? title : ""}</div>
        </div>
        <div
          ref={this.plotRef}
          style={{
            position: "relative",
            width,
            height,
            cursor: isPanning ? "grabbing" : "grab",
          }}
          onMouseMove={this._handleMouseMove.bind(this)}
          onMouseLeave={this._handleMouseLeave.bind(this)}
          onMouseDown={this._handleMouseDown.bind(this)}
        >
          <svg
            ref={this.svgRef}
            width={width}
            height={height}
            role="img"
            aria-label={title || "Doser graph"}
          />
          {hover && (
            <>
              <div
                style={{
                  ...crosshairTyp,
                  left: hover.x,
                  top: margin.top,
                  height: innerH,
                }}
              />
              <div
                style={{
                  ...hoverValueTyp,
                  left: hover.x + 4,
                  padding: 5,
                  border: `1px solid ${graphColor}`,
                  width: 80,
                  background: hoverBg,
                  zIndex: 10,
                }}
              >
                <div>{`${hover.label} ${unit}`}</div>
                <div>{hover.dateStr}</div>
                <div>{hover.timeStr}</div>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }
}
